import { COUNTRY_PREFIXES, getLocalizedCountryName, normalizeCountryCode } from "../../constants/countries";

/**
 * Reusable phone input with international dialing prefix selector.
 *
 * Props:
 *  - prefix: string (e.g. "+39")
 *  - number: string (national subscriber number)
 *  - onChange: (next: { prefix, number }) => void
 *  - required: boolean
 *  - testId: string  ->  produces `${testId}-prefix` and `${testId}-number`
 *  - placeholder: string
 *  - disabled: boolean
 *
 *  NEW (UX improvement — req. Fabrizio Guerra, 23/06/2026):
 *  - customerCountry: ISO code (e.g. "IT") — placed at TOP of dropdown
 *  - dealerCountry: ISO code (e.g. "CH") — placed 2nd only if different
 *  - uiLang: UI language code (e.g. "IT") — drives country-name localisation
 *           and the "Suggested / All countries" optgroup labels
 *
 * Behaviour:
 *  • When `customerCountry` or `dealerCountry` is provided, the dropdown is
 *    split into 2 visually-separated groups:
 *      [Suggeriti]: customer country, then dealer country (if distinct)
 *      [Tutti i paesi]: every other country, sorted alphabetically by
 *      LOCALIZED name in the active UI language.
 *  • Otherwise it falls back to the legacy single-list (LoJack market order),
 *    preserving backward compatibility for callers that don't pass context.
 */

// Localized labels for the two optgroups (12 languages supported by the app)
const GROUP_LABELS = {
  IT: { suggested: "— Suggeriti —", all: "— Tutti i paesi —" },
  UK: { suggested: "— Suggested —", all: "— All countries —" },
  FR: { suggested: "— Suggeres —", all: "— Tous les pays —" },
  ES: { suggested: "— Sugeridos —", all: "— Todos los paises —" },
  DE: { suggested: "— Empfohlen —", all: "— Alle Lander —" },
  NL: { suggested: "— Aanbevolen —", all: "— Alle landen —" },
  PT: { suggested: "— Sugeridos —", all: "— Todos os paises —" },
  PL: { suggested: "— Sugerowane —", all: "— Wszystkie kraje —" },
  SE: { suggested: "— Forslag —", all: "— Alla lander —" },
  NO: { suggested: "— Forslag —", all: "— Alle land —" },
  FI: { suggested: "— Ehdotetut —", all: "— Kaikki maat —" },
  DK: { suggested: "— Forslag —", all: "— Alle lande —" },
  CZ: { suggested: "— Doporucene —", all: "— Vsechny zeme —" },
  GR: { suggested: "— Protasis —", all: "— Oles oi xores —" },
};

const buildOptionLabel = (c, uiLang) => {
  const localizedName = getLocalizedCountryName(c.code, uiLang) || c.name;
  return `${c.flag} ${c.prefix} · ${localizedName}`;
};

const buildOrderedList = (customerCountry, dealerCountry, uiLang) => {
  const useGrouping = Boolean(customerCountry || dealerCountry);
  if (!useGrouping) {
    // Legacy behaviour: single flat list in hardcoded market order
    return { grouped: false, items: COUNTRY_PREFIXES };
  }

  // Fabrizio bug 06/07/2026 — Il DB salva ``GB``/``IE``/``GI`` (ISO)
  // mentre ``COUNTRY_PREFIXES`` usa i codici legacy ``UK``/``IRL``/``GBZ``.
  // Normalizziamo qui in modo che il group "Suggeriti" venga popolato
  // correttamente anche per i dealer/customer inglesi/irlandesi/gibraltar.
  const normCustomer = normalizeCountryCode(customerCountry);
  const normDealer = normalizeCountryCode(dealerCountry);

  // Find the actual entries (may be undefined if code unknown)
  const customerEntry = COUNTRY_PREFIXES.find((c) => c.code === normCustomer);
  const dealerEntry =
    normDealer && normDealer !== normCustomer
      ? COUNTRY_PREFIXES.find((c) => c.code === normDealer)
      : null;

  const suggested = [customerEntry, dealerEntry].filter(Boolean);
  const suggestedCodes = new Set(suggested.map((c) => c.code));
  const rest = COUNTRY_PREFIXES.filter((c) => !suggestedCodes.has(c.code))
    .map((c) => ({ ...c, _label: buildOptionLabel(c, uiLang) }))
    .sort((a, b) => a._label.localeCompare(b._label, undefined, { sensitivity: "base" }));

  return { grouped: true, suggested, rest };
};

export const PhoneInput = ({
  prefix = "+39",
  number = "",
  onChange,
  required = false,
  testId = "phone",
  placeholder = "es. 3401234567",
  disabled = false,
  customerCountry,
  dealerCountry,
  uiLang = "IT",
}) => {
  const handlePrefix = (e) => onChange({ prefix: e.target.value, number });
  const handleNumber = (e) =>
    onChange({ prefix, number: e.target.value.replace(/[^\d\s]/g, "") });

  const list = buildOrderedList(customerCountry, dealerCountry, uiLang);
  const labels = GROUP_LABELS[uiLang] || GROUP_LABELS.UK;

  const renderOption = (c) => (
    <option
      key={`${c.code}-${c.prefix}`}
      value={c.prefix}
      style={{ backgroundColor: "#111" }}
    >
      {buildOptionLabel(c, uiLang)}
    </option>
  );

  return (
    <div className="flex gap-2">
      <select
        value={prefix}
        onChange={handlePrefix}
        disabled={disabled}
        className="px-2 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none text-sm min-w-[140px] disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={`${testId}-prefix`}
      >
        {list.grouped ? (
          <>
            {list.suggested.length > 0 && (
              <optgroup label={labels.suggested} data-testid={`${testId}-prefix-group-suggested`}>
                {list.suggested.map(renderOption)}
              </optgroup>
            )}
            <optgroup label={labels.all} data-testid={`${testId}-prefix-group-all`}>
              {list.rest.map(renderOption)}
            </optgroup>
          </>
        ) : (
          COUNTRY_PREFIXES.map(renderOption)
        )}
      </select>
      <input
        type="tel"
        value={number}
        onChange={handleNumber}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        className="flex-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={`${testId}-number`}
      />
    </div>
  );
};

export default PhoneInput;
